import type { CollectionFieldPath } from '#layers/data-model/public';
import type { RelatedItemOperator } from '#layers/filters/components/Filter/utils/related-item-match';
import type { RelatedItemTarget } from '#layers/filters/components/Filter/utils/related-item-target';
import { FilterOperator, useWorkspaceSchemaStore } from '#layers/data-model/public';
import { isPrimitiveFieldPath } from '#layers/filters/components/Filter/utils/filter-path';
import { injectFilterSchemaContext } from '#layers/filters/components/Filter/utils/filter-schema-context';
import { relatedItemKeyFields, resolveSelectedRelationTarget, serializeRelatedItemMatch } from '#layers/filters/components/Filter/utils/related-item-target';
import { useFilterGate } from './use-filter-gate';

/**
 * Whether a related-item match may use an operator: checks the clause the related-item serializer
 * produces for placeholder keys, so foreign key columns, junction hops, quantifiers, and composite-key
 * combinators follow the one owner of that shape.
 */
export function useRelatedItemGate() {
	const gate = useFilterGate();
	const schema = injectFilterSchemaContext();
	const workspaceSchema = useWorkspaceSchemaStore();

	function allowsRelatedItem(target: RelatedItemTarget, operator: RelatedItemOperator) {
		if (!gate.isGated()) return true;
		// Operators and combinators do not depend on the keys' values, but a membership match over a
		// composite key only combines keys with `_or` once it holds several.
		const placeholder = Object.fromEntries(relatedItemKeyFields(target).map((field) => [field.apiName, '']));
		const isMembership = operator === FilterOperator.IN || operator === FilterOperator.NIN;
		return gate.allowsClause(serializeRelatedItemMatch(target, operator, isMembership ? [placeholder, placeholder] : [placeholder]));
	}

	/**
	 * Whether a picker may select a path: a primitive when the gate allows it, a relation when the match
	 * its creation emits is allowed — a membership match for a to-many relation, an identity or
	 * membership match for a to-one relation.
	 */
	function allowsFilterPath(path: CollectionFieldPath) {
		if (!gate.isGated()) return true;
		if (isPrimitiveFieldPath(path, schema)) return gate.allowsPath(path);
		const selected = resolveSelectedRelationTarget(path, workspaceSchema);
		if (!selected) return gate.allowsPath(path);
		const operators = selected.isList ? [FilterOperator.IN] : [FilterOperator.EQ, FilterOperator.IN];
		return operators.some((operator) => allowsRelatedItem(selected.target, operator));
	}

	return { allowsRelatedItem, allowsFilterPath };
}
